import { useEffect } from "react";

// Lightweight client-side SEO hook — sets <title>, <meta name=description>,
// <link rel=canonical>, and OpenGraph + Twitter title/description tags per
// page. We do this in the browser (instead of pulling in react-helmet) so
// the bundle stays small; Googlebot does execute JS, so per-page tags are
// crawled correctly. For richer SSR-style indexing we would pre-render via
// craco/next, which is a P1 follow-up.
//
// Usage:
//   useSEO({
//     title: "Pricing — Lumioos",
//     description: "Compare Lumioos plans...",
//     canonical: "https://mylumioos.com/pricing",
//     ogImage: "/og-share.png",
//   });
const SITE = "https://mylumioos.com";
const DEFAULT_OG = "/og-share.png";

function setOrCreateMeta(attr, attrValue, content) {
  let el = document.head.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function useSEO({ title, description, canonical, ogImage, noindex } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      setOrCreateMeta("name", "description", description);
      setOrCreateMeta("property", "og:description", description);
      setOrCreateMeta("name", "twitter:description", description);
    }
    if (title) {
      setOrCreateMeta("property", "og:title", title);
      setOrCreateMeta("name", "twitter:title", title);
    }
    const canonHref = canonical || `${SITE}${typeof window !== "undefined" ? window.location.pathname : ""}`;
    setCanonical(canonHref);
    setOrCreateMeta("property", "og:url", canonHref);

    const img = ogImage || DEFAULT_OG;
    const absImg = img.startsWith("http") ? img : `${SITE}${img}`;
    setOrCreateMeta("property", "og:image", absImg);
    setOrCreateMeta("name", "twitter:image", absImg);

    // Robots — let pages opt out of indexing (e.g. logged-in dashboards)
    if (noindex) {
      setOrCreateMeta("name", "robots", "noindex, nofollow");
    } else {
      // Re-enable indexing if we previously set noindex
      const robotsEl = document.head.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.setAttribute("content", "index, follow");
    }
  }, [title, description, canonical, ogImage, noindex]);
}
